import { Button } from '@understory-io/leaf/button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { defineMessage, FormattedMessage } from 'react-intl';

/**
 * Shared with places that ask the guest to try again without the notice's own
 * button (a step's Continue is the retry there), so they keep its translations:
 * the id is a hash of `defaultMessage` and `description`.
 */
export const AVAILABILITY_UNAVAILABLE_MESSAGE = defineMessage({
  defaultMessage: "We couldn't check availability right now. Please try again.",
  description:
    'Shown where dates or times would be, when availability could not be loaded; the guest can retry',
});

type AvailabilityUnavailableNoticeProps = {
  onRetry: () => void;
  retrying?: boolean;
};

export type { AvailabilityUnavailableNoticeProps };

/**
 * Stands in for dates or times that could not be loaded. Without it a failed
 * availability read looks like an empty or sold-out calendar, and the guest
 * leaves thinking there is nothing to book, when asking again may well work.
 */
export function AvailabilityUnavailableNotice({
  onRetry,
  retrying = false,
}: AvailabilityUnavailableNoticeProps) {
  return (
    <Stack gap="small" align="center" className={noticeStyles}>
      <Text
        as="p"
        role="alert"
        style="body-small-regular"
        className={css({ color: 'content.secondary' })}
      >
        <FormattedMessage {...AVAILABILITY_UNAVAILABLE_MESSAGE} />
      </Text>
      <Button
        appearance="secondary"
        size="small"
        busy={retrying}
        onClick={onRetry}
      >
        <FormattedMessage
          defaultMessage="Try again"
          description="Button that reloads availability after it could not be loaded"
        />
      </Button>
    </Stack>
  );
}

const noticeStyles = css({
  textAlign: 'center',
  paddingBlock: 'small',
});
